import { Box, Center, Divider, Flex } from '@hover/blueprint';
import type {
  AiImageMaterialEdit,
  AiImagePaintEdit,
} from 'mock/generated/types';
import React, { useMemo, useRef } from 'react';
import { generatePath, useParams } from 'react-router';

import {
  useAnchorScroll,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { FeatureFlag } from 'src/shared/constants';
import type {
  AiDisplayImage,
  Maybe,
  Project as ProjectType,
} from 'src/shared/graphql/generated';
import { useGetProjectQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
  useTreatmentWithConfig,
} from 'src/shared/hooks';
import { useIsMobileSize } from 'src/shared/hooks/useIsMobileSize';
import { Route } from 'src/shared/route';

import { FullscreenImage } from '../../FullscreenImage';
import { StackingHeaderProvider } from '../../StackingHeader';
import { NotFoundSplash } from '../../errors';

import { ComingSoon } from './ComingSoon';
import { ConnectPro } from './ConnectPro';
import { ExploreServices } from './ExploreServices';
import {
  HeaderSection,
  PROJECT_DETAILS_PADDING,
  ProjectCardSection,
  ProjectCardWrapper,
} from './ProjectCardSection';
import { ProjectCarousel } from './ProjectCarousel';
import { ProjectDetails } from './ProjectDetails';
import { ProjectHeader } from './ProjectHeader';
import { ProjectSkeleton } from './ProjectSkeleton';
import type { Swatch } from './ProjectSwatches';
import { ProjectSwatch } from './ProjectSwatches';
import { useAvailableProjectCards } from './hooks';

const MOBILE_HEADER_HEIGHT = 84;

export const inferProjectAreasFromSpaceTypes = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): string[] => {
  if (!assets) return [];
  return [
    ...new Set(
      assets
        .filter(({ spaceType }) => !!spaceType)
        .map(({ spaceType }) => spaceType as string),
    ),
  ];
};

const materialAttributes = (
  material: AiImageMaterialEdit | AiImagePaintEdit,
  asset: AiDisplayImage,
) => ({
  thumbnail:
    asset.aiGeneratedImage.imagePath ||
    asset.aiGeneratedImage.generatedImagePath,
  title: material.name,
  uid: `${material.name} ${asset.aiGeneratedImage.id}`,
});

const formatMaterials = (
  addables: AiDisplayImage[],
  samplizeButtonText?: string,
): { paintSwatches: Swatch[]; materialSwatches: Swatch[] } => {
  const paintSwatches: Swatch[] = [];
  const materialSwatches: Swatch[] = [];

  addables.forEach((asset: AiDisplayImage) => {
    if (!asset.aiGeneratedImage.metadata) return;
    const { allEdits } = asset.aiGeneratedImage.metadata;
    if (!allEdits) return;
    Object.values(allEdits).forEach(category => {
      // NOTE: This is pretty messy, but this data structure is also intended to change
      // with the material configurability project, so we should have a cleaner way to reference
      // materials at that point in time
      if (
        !category ||
        typeof category === 'number' ||
        category === 'AiImageEdits'
      ) {
        return;
      }
      if (
        !(
          category.__typename === 'AiImageRegionEdits' ||
          category.__typename === 'AiImageWallEdits'
        )
      ) {
        return;
      }

      const { paint, material } = category;

      if (paint) {
        paintSwatches.push({
          color: paint.hex,
          subtitle: paint.manufacturer,
          linkOutCta: paint.samplizeProductKey ? samplizeButtonText : '',
          linkOutUrl: paint.samplizeProductKey
            ? `https://co.samplize.com/products/${paint.samplizeProductKey}?utm_source=hover&utm_medium=referral&utm_campaign=hover_p1`
            : '',
          ...materialAttributes(paint, asset),
        });
      }

      if (material) {
        materialSwatches.push({
          color: material.hex || undefined,
          image: material.thumbnail || undefined,
          ...materialAttributes(material, asset),
        });
      }
    });
  });
  return {
    paintSwatches,
    materialSwatches,
  };
};

const getProjectImages = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): AiDisplayImage[] => {
  const images: AiDisplayImage[] = [];
  assets?.forEach(asset => {
    images.push(asset);
  }, []);

  return images;
};

const Project = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { projectId } = useParams<{ projectId: string }>();
  const projectNameVisibilityHandler = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const materialsRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const isMobileSize = useIsMobileSize();

  const sectionToRef: Record<string, React.RefObject<HTMLDivElement>> = {
    about: aboutRef,
    materials: materialsRef,
    services: servicesRef,
    help: helpRef,
  };

  const scrollTop = isMobileSize ? -MOBILE_HEADER_HEIGHT : 0;
  const scrollToRef = useAnchorScroll(scrollTop);
  const { setCurrentMenu } = useHomeownerAppContext();

  const { data, loading } = useGetProjectQuery({
    variables: {
      projectId: Number(projectId),
    },
  });

  const { config } = useTreatmentWithConfig<FeatureFlag.ProjectLinkouts>(
    FeatureFlag.Designer.PROJECT_LINKOUTS,
  );

  const { config: comingSoonConfig } =
    useTreatmentWithConfig<FeatureFlag.ComingSoonLinkouts>(
      FeatureFlag.Designer.COMING_SOON,
    );

  useEffectOnMount(() => {
    window.scrollTo(0, 0);
  });

  const project = data?.project as ProjectType | undefined;
  const addables: AiDisplayImage[] = useMemo(
    () => project?.addables || [],
    [project],
  );
  const images = getProjectImages(addables) || [];

  const { paintSwatches, materialSwatches } = useMemo(
    () => formatMaterials(addables, config?.samplize?.buttonText),
    [addables, config],
  );

  const { showComingSoon, showConnectPro, showExploreServices } =
    useAvailableProjectCards();

  if (loading) return <ProjectSkeleton />;
  if (!project) return <NotFoundSplash />;

  const ANCHOR_SECTION_TO_IS_VISIBLE: Record<string, boolean> = {
    about: showComingSoon,
    materials: !!materialSwatches.length,
    services: showExploreServices,
    help: showConnectPro,
  };

  const handleMoreActionsClick = () => {
    setCurrentMenu(HomeownerAppMenuNames.PROJECT_MORE_ACTIONS);
  };

  const handleNavButtonClick = (sectionName: string) => {
    scrollToRef(sectionToRef[sectionName]);
  };

  return (
    <StackingHeaderProvider
      height={{
        desktop: { expanded: 0, collapsed: 0 },
        mobile: { expanded: 80, collapsed: 80 },
      }}
    >
      <ProjectHeader
        id={project.id}
        name={project.name}
        onMoreActionsClick={handleMoreActionsClick}
        projectNameVisibilityHandler={projectNameVisibilityHandler}
      />
      <Center height="100%">
        <Box flexDirection={{ base: 'column', tablet: 'row' }} width="100%">
          <FullscreenImage
            images={images}
            path={generatePath(
              Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
              { projectId, aiDisplayImageId: ':aiDisplayImageId' },
            )}
            returnPath={generatePath(Route.HOMEOWNER_APP_PROJECT.path, {
              projectId,
            })}
          >
            <ProjectCarousel
              addables={project?.addables}
              projectNameVisibilityHandler={projectNameVisibilityHandler}
              width={{ base: '100%', tablet: '50%' }}
            />
          </FullscreenImage>
          <Box
            boxSizing="border-box"
            flexDirection="column"
            overflow="hidden"
            width={{ base: '100%', tablet: '50%' }}
          >
            <Flex
              align="flex-start"
              boxSizing="border-box"
              flexDirection="column"
              gap={0}
              p={PROJECT_DETAILS_PADDING}
              width="100%"
            >
              {/*  Project Details */}
              <ProjectDetails
                areas={inferProjectAreasFromSpaceTypes(project?.addables)}
                name={project.name}
                navItems={Object.keys(ANCHOR_SECTION_TO_IS_VISIBLE).filter(
                  k => !!ANCHOR_SECTION_TO_IS_VISIBLE[k],
                )}
                onNavButtonClick={handleNavButtonClick}
                projectId={project.id}
              />
              {/* coming soon */}
              {ANCHOR_SECTION_TO_IS_VISIBLE.about && (
                <div ref={aboutRef}>
                  <ComingSoon config={comingSoonConfig} projectId={projectId} />
                </div>
              )}
              {/* /coming soon */}
            </Flex>
            <Flex flexDirection="column" mb={700} width="100%">
              <Divider
                display={{ tablet: 'none' }}
                mb={700}
                mt={600}
                width="90%"
              />
              {/* paint */}
              {!!paintSwatches.length && (
                <HeaderSection
                  subtitle={
                    config.samplize?.description ||
                    t('projects.swatch.paint.subtitle')
                  }
                  title={t('projects.swatch.paint.title')}
                />
              )}
              <ProjectCardSection>
                {paintSwatches.map(swatch => (
                  <ProjectCardWrapper key={swatch.uid}>
                    <ProjectSwatch {...swatch} projectId={projectId} />
                  </ProjectCardWrapper>
                ))}
              </ProjectCardSection>
              {/* /paint */}
              {/* materials */}
              {ANCHOR_SECTION_TO_IS_VISIBLE.materials && (
                <div ref={materialsRef}>
                  <HeaderSection
                    subtitle={t('projects.swatch.material.subtitle')}
                    title={t('projects.swatch.material.title')}
                  />
                </div>
              )}
              <ProjectCardSection>
                {materialSwatches.map(swatch => (
                  <ProjectCardWrapper key={swatch.uid}>
                    <ProjectSwatch {...swatch} projectId={projectId} />
                  </ProjectCardWrapper>
                ))}
              </ProjectCardSection>
              {/* /materials */}
              {/* services */}
              {ANCHOR_SECTION_TO_IS_VISIBLE.services && (
                <div ref={servicesRef}>
                  <ExploreServices config={config} projectId={projectId} />
                </div>
              )}
            </Flex>
            {/* /services */}
            {/* help */}
            {ANCHOR_SECTION_TO_IS_VISIBLE.help && (
              <div ref={helpRef}>
                <ConnectPro config={comingSoonConfig} projectId={projectId} />
              </div>
            )}
            {/* /help */}
          </Box>
        </Box>
      </Center>
    </StackingHeaderProvider>
  );
};

export { Project };
